import { useLocation, useMatch } from "react-router";

import Accordion from "components/common/ui/Accordion/Accordion";
import Typography from "components/common/ui/Typography/Typography";
import SpContent from "components/supportingPages/SpContent/SpContent";
import SpMarkdown from "components/supportingPages/SpMarkdown/SpMarkdown";

import { MenuItemData } from "ts/types";

import { wordPressApi } from "app/api/wordpress/wordPressApi";
import SupportingPageLayout from "components/common/layouts/SupportingPageLayout/SupportingPageLayout";
import Page from "components/common/ui/Page/Page";
import SEO from "components/common/ui/SEO/SEO";
import SpNav from "components/supportingPages/SpNav/SpNav";
import usePageContent from "hooks/usePageContent";
import { useEffect, useMemo } from "react";
import { PageType } from "ts/enums";
import getRobotsContent from "utilities/getRobotsContent";
import { StyledWrapper } from "./FaqsPage.Styled";

const FaqsPage = () => {
	const match = useMatch("/:locale/*");
	const uri = match?.params["*"] || "";

	const { data, isLoading } = wordPressApi.useGetPageContentByUriQuery(uri);
	const menuItems = wordPressApi.useGetMenuItemsBySlugQuery("footer");
	const location = useLocation();
	const pageContent = usePageContent();

	useEffect(() => {
		pageContent.scrollToTop();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const parentNavOption = useMemo(() => {
		const parentItem = menuItems.data?.find((menuItem: MenuItemData) => {
			if (menuItem.children.find((child) => child.path.includes(uri))) {
				return menuItem;
			}
		}) as MenuItemData;

		return parentItem;
	}, [menuItems.data]);

	const navOptions = useMemo(() => {
		return (
			parentNavOption?.children.map((child) => {
				return {
					label: child.label,
					value: child.path,
				};
			}) || []
		);
	}, [parentNavOption, menuItems.data]);

	if (!document) {
		return <Page pageType={PageType.PAGE} />;
	}

	return (
		<Page pageType={PageType.PAGE}>
			<SEO
				title={data?.seo.title}
				description={data?.seo.metaDesc}
				keywords={data?.seo.metaKeywords}
				robots={getRobotsContent([
					data?.seo.metaRobotsNofollow,
					'index'
				])}
			/>
			<SupportingPageLayout data={data} loading={isLoading}>
				<SpNav
					className="nav"
					isLoading={menuItems.isLoading}
					title={parentNavOption?.label || ""}
					options={navOptions}
				/>
				<StyledWrapper>
					<SpContent
						className="content"
						isLoading={isLoading}
						content={data?.content}
					/>
					<Accordion>
						{data?.faqs?.map((faq: any, index: number) => (
							<Accordion.Item
								key={index}
								titleComponent={<Typography>{faq.faq}</Typography>}
								bodyComponent={<SpMarkdown html={faq.answer} />}
							/>
						))}
					</Accordion>
				</StyledWrapper>
			</SupportingPageLayout>
		</Page>
	);
};

export default FaqsPage;
